import React from "react";
import { Alert, Checkbox, DatePicker, Flex, Input, Typography } from "antd";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";

type CloneScheduleFormProps = {
	cloneSource: any;
	cloneNameInput: string;
	setCloneNameInput: (value: string) => void;
	cloneNewStart: Dayjs | null;
	setCloneNewStart: (value: Dayjs | null) => void;
	cloneNewEnd: Dayjs | null;
	setCloneNewEnd: (value: Dayjs | null) => void;
	bringForward: boolean;
	setBringForward: (value: boolean) => void;
	onPressEnter: () => void;
};

const CloneScheduleForm: React.FC<CloneScheduleFormProps> = ({
	cloneSource,
	cloneNameInput,
	setCloneNameInput,
	cloneNewStart,
	setCloneNewStart,
	cloneNewEnd,
	setCloneNewEnd,
	bringForward,
	setBringForward,
	onPressEnter,
}) => {
	const origStart = cloneSource ? dayjs(cloneSource.start_time) : null;
	const origEnd   = cloneSource ? dayjs(cloneSource.end_time)   : null;
	const origSpanDays = origStart && origEnd ? origEnd.diff(origStart, "day") : null;
	const newSpanDays  = cloneNewStart && cloneNewEnd ? cloneNewEnd.diff(cloneNewStart, "day") : null;

	const dateError =
		cloneNewStart && cloneNewEnd && !cloneNewEnd.isAfter(cloneNewStart)
			? "End date must be after start date."
			: null;

	const origContentEnd =
		origSpanDays !== null && cloneNewStart
			? cloneNewStart.add(origSpanDays, "day")
			: null;

	const emptyDays =
		newSpanDays !== null && origSpanDays !== null && newSpanDays > origSpanDays
			? newSpanDays - origSpanDays
			: null;

	const spanWarning =
		bringForward &&
		cloneNewStart &&
		origSpanDays !== null &&
		newSpanDays !== null &&
		newSpanDays !== origSpanDays
			? newSpanDays > origSpanDays
				? `Content covers ${origSpanDays} days (until ${origContentEnd?.format("MMM D, YYYY")}). The last ${emptyDays} day${emptyDays === 1 ? "" : "s"} of the new window will be empty.`
				: `New window is ${newSpanDays} days. Only the first ${newSpanDays} days of content will be copied. The rest will be discarded.`
			: null;

	return (
		<Flex vertical gap={16} style={{ marginTop: 8 }}>
			<div>
				<Typography.Text type="secondary">Cloning: </Typography.Text>
				<Typography.Text strong>{cloneSource?.title}</Typography.Text>
				{origStart && origEnd && (
					<>
						<br />
						<Typography.Text type="secondary">
							Original: {origStart.format("MMM D, YYYY")} → {origEnd.format("MMM D, YYYY")}
						</Typography.Text>
					</>
				)}
			</div>

			<div>
				<Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
					Schedule Name
				</Typography.Text>
				<Input
					value={cloneNameInput}
					onChange={e => setCloneNameInput(e.target.value)}
					onPressEnter={onPressEnter}
					onFocus={e => e.target.select()}
					placeholder="Enter a name for the cloned schedule"
					autoFocus
				/>
			</div>

			<Flex gap={12}>
				<div style={{ flex: 1 }}>
					<Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
						New Start Date
					</Typography.Text>
					<DatePicker
						style={{ width: "100%" }}
						value={cloneNewStart}
						onChange={val => setCloneNewStart(val)}
						format="YYYY-MM-DD"
						disabledDate={d => !!cloneNewEnd && d.isAfter(cloneNewEnd, "day")}
					/>
				</div>
				<div style={{ flex: 1 }}>
					<Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
						New End Date
					</Typography.Text>
					<DatePicker
						style={{ width: "100%" }}
						value={cloneNewEnd}
						onChange={val => setCloneNewEnd(val)}
						format="YYYY-MM-DD"
						disabledDate={d => !!cloneNewStart && d.isBefore(cloneNewStart, "day")}
					/>
				</div>
			</Flex>

			{dateError && <Alert type="error" message={dateError} showIcon />}

			<Checkbox checked={bringForward} onChange={e => setBringForward(e.target.checked)}>
				Bring forward all content to new dates
			</Checkbox>

			{spanWarning && <Alert type="warning" message={spanWarning} showIcon />}
		</Flex>
	);
};

export default CloneScheduleForm;
